import { PromotionStage } from "../promo_creation/types"

export const ALL_PROMOTION_STAGES: PromotionStage[] = ["Pending", "Submitted", "Approved", "Running", "Completed", "Cancelled"]

/**
 * Mirror of the server's stage-transition rules (`promo_access::allowed_stage_targets`).
 * Brokers submit plans for brand review but never approve them: they may submit a pending
 * promotion, withdraw a submission, or cancel a pending plan. Everyone else moves freely.
 */
export function allowed_stage_targets(role: string | undefined, from: PromotionStage): PromotionStage[] {
  if (role === "broker") {
    if (from === "Pending") return ["Submitted", "Cancelled"]
    if (from === "Submitted") return ["Pending"]
    return []
  }
  return ALL_PROMOTION_STAGES.filter(stage => stage !== from)
}

/** Options for a stage select: the current stage plus every stage this role may move to. */
export function stage_options(role: string | undefined, current: PromotionStage): PromotionStage[] {
  const targets = allowed_stage_targets(role, current)
  return ALL_PROMOTION_STAGES.filter(stage => stage === current || targets.includes(stage))
}

/** Mirror of the server's `promo_access::can_edit_fields`: brokers can't edit approved plans. */
export function can_edit_promotion(role: string | undefined, stage: PromotionStage): boolean {
  if (role === "broker") return stage === "Pending" || stage === "Submitted"
  return true
}

if (import.meta.vitest) {
  const { describe, expect, it } = import.meta.vitest

  describe("allowed_stage_targets", () => {
    it("lets brokers submit, withdraw, and cancel but never approve", () => {
      expect(allowed_stage_targets("broker", "Pending")).toEqual(["Submitted", "Cancelled"])
      expect(allowed_stage_targets("broker", "Submitted")).toEqual(["Pending"])
      expect(allowed_stage_targets("broker", "Approved")).toEqual([])
    })

    it("lets other roles move freely", () => {
      expect(allowed_stage_targets("org_admin", "Pending")).toContain("Approved")
      expect(allowed_stage_targets(undefined, "Approved")).toContain("Cancelled")
    })
  })

  describe("stage_options", () => {
    it("always includes the current stage", () => {
      expect(stage_options("broker", "Approved")).toEqual(["Approved"])
      expect(stage_options("broker", "Pending")).toEqual(["Pending", "Submitted", "Cancelled"])
      expect(stage_options("user", "Pending")).toEqual(ALL_PROMOTION_STAGES)
    })
  })

  describe("can_edit_promotion", () => {
    it("blocks brokers once approved", () => {
      expect(can_edit_promotion("broker", "Pending")).toBe(true)
      expect(can_edit_promotion("broker", "Submitted")).toBe(true)
      expect(can_edit_promotion("broker", "Approved")).toBe(false)
      expect(can_edit_promotion("org_admin", "Approved")).toBe(true)
    })
  })
}
